// Detection findings — paginated, filterable list of every open
// scanner finding the caller can see. Tier 0 renders the table
// chrome and a friendly empty state; once Tier 1 lands and scanners
// run, rows populate without code changes.

import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { LayoutDashboard, ScanLine, Search, ShieldAlert } from 'lucide-react';
import { AppShell } from '../../components/AppShell';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import {
  bulkPatchFindings,
  useFindings,
  type FindingSeverity,
  type FindingStatus,
} from '../../api/detection';

const selectClass =
  'h-9 px-3 rounded-md bg-bg-inset text-fg border border-border focus:border-accent focus:bg-bg-elevated transition-colors text-sm';

function severityTone(s: FindingSeverity): 'danger' | 'warning' | 'accent' | 'neutral' | 'success' {
  switch (s) {
    case 'critical': return 'danger';
    case 'high':     return 'danger';
    case 'medium':   return 'warning';
    case 'low':      return 'accent';
    case 'info':     return 'neutral';
  }
}

function statusTone(s: FindingStatus): 'danger' | 'warning' | 'success' | 'neutral' {
  switch (s) {
    case 'open':         return 'danger';
    case 'acknowledged': return 'warning';
    case 'suppressed':   return 'neutral';
    case 'fixed':        return 'success';
  }
}

export function DetectionFindings() {
  const [params, setParams] = useSearchParams();
  const status = (params.get('status') as FindingStatus | null) ?? undefined;
  const severity = (params.get('severity') as FindingSeverity | null) ?? undefined;
  const scanner = params.get('scanner') ?? undefined;
  const project = params.get('project') ?? undefined;
  const slaBreached = params.get('sla') === 'breached';

  // The useFindings hook doesn't yet accept ?sla=breached as a typed
  // filter; the URL is passed through verbatim to the API client,
  // so we rely on the params layer. When the user toggles the chip,
  // we update the URL — useFindings re-fetches automatically since
  // its key changes via the searchParams-aware router state.
  const { data: findings, isLoading, mutate } = useFindings({ status, severity, scanner, project });
  const [search, setSearch] = useState('');
  // v177 Tier 4 — bulk-triage selection state. Selected IDs persist
  // across filter changes within a session; clearing is explicit.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkErr, setBulkErr] = useState<string | null>(null);
  const [bulkReason, setBulkReason] = useState('');
  const [bulkUntil, setBulkUntil] = useState(''); // datetime-local string

  const toggleSel = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleSelAll = (allRows: { id: string }[]) => {
    if (selected.size === allRows.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(allRows.map((r) => r.id)));
    }
  };

  const runBulk = async (st: FindingStatus) => {
    if (selected.size === 0) return;
    if (st === 'suppressed' && !bulkReason.trim()) {
      setBulkErr('Reason required when suppressing.');
      return;
    }
    setBulkBusy(true);
    setBulkErr(null);
    try {
      const ids = Array.from(selected);
      await bulkPatchFindings({
        ids,
        status: st,
        suppress_reason: st === 'suppressed' ? bulkReason : undefined,
        suppressed_until: st === 'suppressed' && bulkUntil
          ? new Date(bulkUntil).toISOString()
          : undefined,
      });
      setSelected(new Set());
      setBulkReason('');
      setBulkUntil('');
      await mutate();
    } catch (e) {
      setBulkErr(String(e));
    } finally {
      setBulkBusy(false);
    }
  };

  const filtered = (findings ?? []).filter((f) => {
    if (slaBreached && !f.sla_breached_at) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      f.title.toLowerCase().includes(s) ||
      f.rule_id.toLowerCase().includes(s) ||
      f.artifact_path.toLowerCase().includes(s)
    );
  });

  const setParam = (k: string, v: string | undefined) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next, { replace: true });
  };

  return (
    <AppShell>
      <div className="space-y-6">
        <div>
          <p className="orb-eyebrow text-accent/80 mb-2">Detection</p>
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-md bg-accent/10 border border-accent/30 flex items-center justify-center shrink-0">
              <ScanLine className="h-5 w-5 text-accent" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-display font-semibold text-fg leading-tight">Findings</h1>
              <p className="text-fg-muted text-sm mt-2 max-w-2xl">
                Every scanner finding across the projects you can see.
                Click a row to drill down, ack, or suppress. Severity sort
                first, then last-seen.
              </p>
            </div>
            <Link
              to="/detection/dashboard"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm border border-border hover:bg-bg-hover/50 text-fg-muted hover:text-fg"
            >
              <LayoutDashboard className="h-4 w-4" />
              Dashboard
            </Link>
          </div>
        </div>

        <Card>
          <CardHeader
            title={`${filtered.length} finding${filtered.length === 1 ? '' : 's'}`}
            description={isLoading ? <Spinner size="xs" /> : undefined}
            actions={
              <div className="flex items-center gap-2 flex-wrap">
                <select
                  value={status ?? ''}
                  onChange={(e) => setParam('status', e.target.value || undefined)}
                  className={selectClass}
                >
                  <option value="">all status</option>
                  <option value="open">open</option>
                  <option value="acknowledged">acknowledged</option>
                  <option value="suppressed">suppressed</option>
                  <option value="fixed">fixed</option>
                </select>
                <select
                  value={severity ?? ''}
                  onChange={(e) => setParam('severity', e.target.value || undefined)}
                  className={selectClass}
                >
                  <option value="">all severity</option>
                  <option value="critical">critical</option>
                  <option value="high">high</option>
                  <option value="medium">medium</option>
                  <option value="low">low</option>
                  <option value="info">info</option>
                </select>
                <button
                  type="button"
                  onClick={() => setParam('sla', slaBreached ? undefined : 'breached')}
                  className={
                    'h-9 px-3 rounded-md border text-sm transition-colors ' +
                    (slaBreached
                      ? 'bg-danger/10 border-danger/40 text-danger'
                      : 'bg-bg-inset border-border text-fg-muted hover:text-fg')
                  }
                  title="show only findings whose SLA has been breached"
                >
                  {slaBreached ? '× SLA breached only' : 'SLA breached only'}
                </button>
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-fg-subtle" />
                  <input
                    type="text"
                    placeholder="rule / title / path"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className={selectClass + ' pl-8 w-64'}
                  />
                </div>
              </div>
            }
          />
          {selected.size > 0 && (
            <div className="px-5 py-3 border-b border-border bg-accent/5 flex items-center gap-3 flex-wrap">
              <span className="text-sm text-fg font-medium">
                {selected.size} selected
              </span>
              <input
                type="text"
                placeholder="reason (required for suppress)"
                value={bulkReason}
                onChange={(e) => setBulkReason(e.target.value)}
                className={selectClass + ' flex-1 min-w-[16rem]'}
              />
              <input
                type="datetime-local"
                title="suppressed until (optional)"
                value={bulkUntil}
                onChange={(e) => setBulkUntil(e.target.value)}
                className={selectClass}
              />
              <Button variant="secondary" disabled={bulkBusy} onClick={() => runBulk('acknowledged')}>
                Ack
              </Button>
              <Button variant="secondary" disabled={bulkBusy || !bulkReason.trim()} onClick={() => runBulk('suppressed')}>
                Suppress
              </Button>
              <Button variant="secondary" disabled={bulkBusy} onClick={() => runBulk('fixed')}>
                Fixed
              </Button>
              <Button variant="ghost" disabled={bulkBusy} onClick={() => setSelected(new Set())}>
                Clear
              </Button>
              {bulkErr && <span className="text-xs text-danger font-mono w-full">{bulkErr}</span>}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="px-3 py-3 w-8">
                    <input
                      type="checkbox"
                      checked={filtered.length > 0 && selected.size === filtered.length}
                      onChange={() => toggleSelAll(filtered)}
                      aria-label="Select all"
                    />
                  </th>
                  <th className="px-3 py-3 font-medium text-fg-muted">Severity</th>
                  <th className="px-3 py-3 font-medium text-fg-muted">Title</th>
                  <th className="px-3 py-3 font-medium text-fg-muted">Rule</th>
                  <th className="px-3 py-3 font-medium text-fg-muted">Artifact</th>
                  <th className="px-3 py-3 font-medium text-fg-muted">Project</th>
                  <th className="px-3 py-3 font-medium text-fg-muted">Status</th>
                  <th className="px-3 py-3 font-medium text-fg-muted">Last seen</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length ? (
                  filtered.map((f) => (
                    <tr
                      key={f.id}
                      className="border-b border-border/60 last:border-0 hover:bg-bg-hover/40"
                    >
                      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selected.has(f.id)}
                          onChange={() => toggleSel(f.id)}
                          aria-label="Select finding"
                        />
                      </td>
                      <td className="px-3 py-3 cursor-pointer" onClick={() => (globalThis.location.href = `/detection/findings/${f.id}`)}>
                        <div className="flex items-center gap-1.5">
                          <Badge tone={severityTone(f.severity)}>{f.severity}</Badge>
                          {f.sla_breached_at && (
                            <span
                              className="text-[10px] uppercase tracking-wide font-semibold text-danger bg-danger/10 border border-danger/30 px-1.5 py-0.5 rounded"
                              title={`SLA breach on ${new Date(f.sla_breached_at).toLocaleString()} — open ${f.age_days ?? '?'} days`}
                            >
                              SLA
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-fg cursor-pointer" onClick={() => (globalThis.location.href = `/detection/findings/${f.id}`)}>
                        {f.title}
                      </td>
                      <td className="px-3 py-3 font-mono text-xs text-accent cursor-pointer" onClick={() => (globalThis.location.href = `/detection/findings/${f.id}`)}>
                        {f.rule_id}
                      </td>
                      <td className="px-3 py-3 text-fg-muted text-xs font-mono break-all cursor-pointer" title={f.artifact_path} onClick={() => (globalThis.location.href = `/detection/findings/${f.id}`)}>
                        {f.artifact_path}
                      </td>
                      <td className="px-3 py-3 text-fg-muted cursor-pointer" onClick={() => (globalThis.location.href = `/detection/findings/${f.id}`)}>{f.project_slug}</td>
                      <td className="px-3 py-3 cursor-pointer" onClick={() => (globalThis.location.href = `/detection/findings/${f.id}`)}>
                        <Badge tone={statusTone(f.status)}>{f.status}</Badge>
                      </td>
                      <td className="px-3 py-3 text-fg-muted text-xs whitespace-nowrap cursor-pointer" onClick={() => (globalThis.location.href = `/detection/findings/${f.id}`)}>
                        {new Date(f.last_seen_at).toLocaleString()}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8}>
                      <EmptyState />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        <Card className="border-accent/30 bg-accent/5">
          <CardBody>
            <div className="flex items-start gap-3">
              <ShieldAlert className="h-5 w-5 text-accent mt-0.5 shrink-0" />
              <div className="text-sm text-fg-muted">
                <span className="text-fg font-medium">Detection vs Security.</span>{' '}
                Detection findings are <em>discovered</em> by scanners against the
                content of your artifacts. To <em>act</em> on a finding (block all
                downloads of the affected artifact), pivot to{' '}
                <Link to="/security" className="text-accent hover:underline">Security</Link>{' '}
                and create a block — the finding's drill-down has a one-click
                handover.
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
    </AppShell>
  );
}

function EmptyState() {
  return (
    <div className="px-5 py-16 text-center text-fg-muted">
      <ScanLine className="h-10 w-10 mx-auto mb-3 text-fg-subtle" />
      <p className="text-fg font-medium">No findings yet.</p>
      <p className="text-sm mt-1.5 max-w-md mx-auto">
        Either nothing has been flagged, or scanners aren't enabled in this
        build. Admins can check the{' '}
        <Link to="/detection/scanners" className="text-accent hover:underline">
          Scanners registry
        </Link>{' '}
        to see what's available.
      </p>
    </div>
  );
}
